/**
 * Feature Flags Signal Store
 *
 * Manages feature flag state with:
 * - HTTP fetch with exponential backoff retry (100ms, 200ms, 400ms)
 * - Ready state for guards to wait on
 * - Environment fallback for frontend-only flags
 * - WebSocket-triggered reload
 */
import { computed, inject, Signal } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, timer } from 'rxjs';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';
import environment from '@environments/environment';
import { DEFAULT_FEATURE_FLAGS_STATE } from '../constants';
import { FeatureFlagsService } from '../service';
import { FeatureFlag, FeatureFlagSubFeatureMap } from '../enums';
import type { FeatureFlagDetail } from '../types';

export const FEATURE_FLAGS_SIGNAL_STORE = signalStore(
  { providedIn: 'root' },
  withState(() => inject(DEFAULT_FEATURE_FLAGS_STATE)),
  withComputed(({ flags, isLoading, isReady, error, updatingFlag }) => ({
    $flags: computed(() => flags()),
    $displayedFlags: computed<Record<string, FeatureFlagDetail>>(() => {
      const allFlags = flags();
      return Object.entries(allFlags)
        .filter(([_, flag]) => flag.display_in_ui)
        .reduce(
          (acc, [key, flag]) => ({ ...acc, [key]: flag }),
          {} as Record<string, FeatureFlagDetail>,
        );
    }),
    $isLoading: computed(() => isLoading()),
    $isReady: computed(() => isReady()),
    $error: computed(() => error()),
    $updatingFlag: computed(() => updatingFlag()),
  })),
  withMethods((store) => {
    const _featureFlagsService = inject(FeatureFlagsService);

    // Cache for memoized feature flag selectors
    const _featureFlagSelectors = new Map<FeatureFlag, Signal<boolean>>();

    // Cache for memoized sub-feature selectors, keyed by `flagName:featureKey`
    const _subFeatureSelectors = new Map<string, Signal<boolean>>();

    const _loadFeatureFlags = rxMethod<void>(
      pipe(
        tap(() => patchState(store, { error: null, isLoading: true })),
        switchMap(() =>
          _featureFlagsService.getFeatureFlags().pipe(
            retry({
              count: 3,
              delay: (error, retryCount) => {
                // Exponential backoff: 100ms, 200ms, 400ms
                const delayMs = 100 * Math.pow(2, retryCount);
                console.log(
                  `[FeatureFlags] Retry ${retryCount} after ${delayMs}ms`,
                  error,
                );
                return timer(delayMs);
              },
            }),
            tapResponse({
              next: (response) => {
                patchState(store, {
                  flags: response.feature_flags,
                  isReady: true,
                  isLoading: false,
                  error: null,
                });
                console.log(
                  '[FeatureFlags] Loaded successfully:',
                  response.feature_flags,
                );
              },
              error: (error: Error) => {
                patchState(store, {
                  error,
                  isLoading: false,
                  isReady: true, // Set ready even on error so guards don't block forever
                });
                console.error(
                  '[FeatureFlags] Failed to load after retries:',
                  error,
                );
              },
            }),
          ),
        ),
      ),
    );

    return {
      /**
       * Load feature flags from backend API
       * Automatically retries with exponential backoff on failure
       */
      loadFeatureFlags: () => _loadFeatureFlags(),

      /**
       * Update a feature flag via API with optimistic update
       * Automatically reverts state if API call fails
       *
       * @param flagKey - Feature flag key to update
       * @param enabled - New enabled state
       * @param reason - Reason for the change (required for audit trail)
       */
      updateFeatureFlag: (
        flagKey: string,
        enabled: boolean,
        reason: string,
      ): void => {
        // Set updating state
        patchState(store, { updatingFlag: flagKey });

        _featureFlagsService
          .updateFeatureFlag(flagKey, enabled, reason)
          .pipe(
            tap(() => {
              console.log(`[FeatureFlags] Updated ${flagKey}=${enabled}`);
              const currentFlags = store.$flags();
              const flagToUpdate = currentFlags[flagKey];
              patchState(store, {
                updatingFlag: null,
                flags: {
                  ...currentFlags,
                  [flagKey]: { ...flagToUpdate, enabled },
                },
              });
            }),
            catchError((error) => {
              console.error(
                `[FeatureFlags] Failed to update ${flagKey}:`,
                error,
              );
              patchState(store, { updatingFlag: null });
              // Revert by reloading from backend
              _loadFeatureFlags();
              throw error;
            }),
          )
          .subscribe();
      },

      /**
       * Get a reactive signal for a specific feature flag
       * Returns a memoized computed signal that updates when flags change
       *
       * @param flagName - Feature flag to select
       * @returns Signal<boolean> that reactively updates
       */
      $selectFeatureFlag: (flagName: FeatureFlag): Signal<boolean> => {
        // Return cached selector if it exists
        if (_featureFlagSelectors.has(flagName)) {
          return _featureFlagSelectors.get(flagName)!;
        }

        // Create new computed signal
        const selector = computed(() => {
          const flags = store.$flags();

          // Check backend flags first
          if (flagName in flags) {
            return flags[flagName].enabled;
          }

          // Fallback to environment for frontend-only flags
          if (flagName in environment) {
            return !!(environment as any)[flagName];
          }

          return false;
        });

        // Cache and return
        _featureFlagSelectors.set(flagName, selector);
        return selector;
      },

      /**
       * Get sub-feature nested under a flag's `features`
       * map. Requires the parent flag itself to be enabled.
       * @param flagName - Parent feature flag to select; must have an entry in `FeatureFlagSubFeatureMap`
       * @param featureKey - Sub-feature key inside that flag's `features` map, typed to the flag's own sub-feature enum
       * @returns Signal<boolean> that reactively updates
       */
      $selectFeature: <F extends keyof FeatureFlagSubFeatureMap>(
        flagName: F,
        featureKey: FeatureFlagSubFeatureMap[F],
      ): Signal<boolean> => {
        const cacheKey = `${flagName}:${featureKey}`;
        if (_subFeatureSelectors.has(cacheKey)) {
          return _subFeatureSelectors.get(cacheKey)!;
        }

        const selector = computed(() => {
          const flag = store.$flags()[flagName];
          return !!flag?.enabled && !!flag?.features?.[featureKey];
        });

        _subFeatureSelectors.set(cacheKey, selector);
        return selector;
      },
    };
  }),
);

export type FeatureFlagsStore = InstanceType<typeof FEATURE_FLAGS_SIGNAL_STORE>;
